import styled from 'styled-components';

export const ProjectContainer = styled.div`
    min-height: calc(100vh - 41px);
    background: ${props => props.isColor ? props.background : 'none'};
    background-image: ${props => props.isColor ? 'none' : props.background ? `url(${props.background})` : 'none'};
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    position: relative;
    display: flex;
    flex-direction: column;
    height: ${props => props.limitColumnHeight ? 'calc(100vh - 41px)' : 'auto'};
    overflow: ${props => props.limitColumnHeight ? 'hidden' : 'visible'};
    margin-top: 41px;
    color: ${props => props.fontColor || '#B6C2CF'} !important;

    /* Problematic global overrides removed:
    * {
        color: ${props => props.fontColor || '#B6C2CF'} !important;
    }

    input, textarea, button {
        color: inherit !important;
    }
    */

    a {
        color: ${props => props.fontColor || '#B6C2CF'} !important;
        &:hover {
            color: ${props => props.fontColor || '#B6C2CF'} !important;
            opacity: 0.8;
        }
    }

    svg {
        color: ${props => props.fontColor || '#B6C2CF'} !important;
    }
`;

export const ContentContainer = styled.div`
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: ${props => props.limitColumnHeight ? 'hidden' : 'visible'};
`;

export const BackgroundCredit = styled.div`
  position: fixed;
  bottom: 8px;
  right: 8px;
  color: white;
  font-size: 12px;
  opacity: 0.8;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  a {
    color: white;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const ProjectHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 6px 24px;
  backdrop-filter: blur(10px);
  background: rgba(0, 0, 0, 0.6);
`;

export const ProjectTitleWrapper = styled.div`
    display: flex; /* This allows TitleSection and potentially other direct children to be flex items */
    position: relative;
    /* Removed align-items, justify-content, gap as they are now handled by TitleControlsLine or not needed here */
    margin-bottom: ${props => props.isCollapsed ? '0' : '16px'};
`;

export const TitleControlsLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

export const TitleSection = styled.div`
  display: flex; /* Added to make its children (TitleControlsLine, tagline, etc.) stack as flex items */
  flex-direction: column; /* Stack children vertically */
  flex-grow: 1; /* Allow TitleSection to take available width within ProjectTitleWrapper */
  min-width: 0; /* Important for deeply nested flex items or items with long content */
  /* margin-bottom is removed as ProjectTitleWrapper handles the overall component bottom margin */
`;

export const ProjectTitle = styled.input`
  background: transparent;
  border: none;
  font-size: 18px;
  font-weight: bold;
  color: #fff;
  /* width: 100%; Remove fixed width, flex properties will control it */
  flex-grow: 1; /* Added to take available space in TitleControlsLine */
  min-width: 0; /* Added for proper flex sizing and truncation */
  padding: ${props => props.isCollapsed ? '0' : '4px'};
  border-radius: 3px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: block; /* Ensure it behaves as a block for overflow to work consistently */

  &:hover {
    background: ${props => props.readOnly ? 'transparent' : 'rgba(255, 255, 255, 0.5)'};
  }

  &:focus {
    outline: none;
  }
`;

export const ProjectTagline = styled.textarea`
  background: transparent;
  border: none;
  font-size: 16px;
  color: #bbb;
  width: 100%;
  padding: 4px;
  border-radius: 3px;
  resize: none; /* Disable manual resizing */
  overflow-y: hidden; /* We'll manage height programmatically */
  min-height: 26px; /* Approximate original input height (16px font + 2*4px padding + 2*1px border if any, adjust as needed) */
  line-height: 1.4; /* Adjust for better text spacing */
  box-sizing: border-box; /* Ensure padding and border are included in the element's total width and height */
  font-family: inherit; /* Ensure it uses the same font as other inputs */

  &:hover {
    background: ${props => props.readOnly ? 'transparent' : 'rgba(255, 255, 255, 0.5)'};
  }

  &:focus {
    outline: none;
  }
`;

export const ProjectDescription = styled.div`
    background: transparent;
    border: none;
    font-size: 14px;
    color: #fff;
    width: 100%;
    padding: 4px;
    resize: none;
    border-radius: 3px;
    line-height: 1.5;
    min-height: 60px;
    max-height: calc(100vh - 300px);
    overflow-y: auto;
    position: relative; /* Needed for positioning the edit button */
    cursor: pointer; /* Indicate it's clickable to enter edit mode */

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }

    /* Style for the content inside the div */
    div {
        min-height: 60px; /* Ensure consistent height */
    }

    /* Placeholder style when content is empty */
    span {
        color: #6B778C;
    }
`;

export const DescriptionWrapper = styled.div`
    position: relative;
    margin-bottom: 16px;
    .ql-editor {
        min-height: 60px;
        max-height: calc(100vh - 300px);
        overflow-y: auto;
    }
`;

export const UrlDisplayWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%; /* Ensure it takes full width */
`;

export const EditButton = styled.button`
    position: absolute;
    top: 0;
    right: 8px;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 3px;
    color: #fff;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;

    ${DescriptionWrapper}:hover &,
    ${UrlDisplayWrapper}:hover & { /* Apply hover effect for UrlDisplayWrapper */
        opacity: 1;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.3);
    }
`;

export const QuillEditorContainer = styled.div`
  .ql-toolbar {
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
  }

  .ql-container {
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    font-size: 14px;
    min-height: 60px;
  }

  .ql-editor {
    min-height: 60px;
    color: #fff;
  }

  .ql-editor.ql-blank::before {
    color: #bbb;
  }

  /* Minimalistic toolbar styling */
  .ql-toolbar.ql-snow .ql-formats {
    margin-right: 8px;
  }

  .ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-options {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  .ql-toolbar.ql-snow .ql-picker-options .ql-picker-item:hover {
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
  }

  .ql-toolbar.ql-snow .ql-stroke {
    stroke: #fff;
  }

  .ql-toolbar.ql.snow .ql-fill {
    fill: #fff;
  }

  .ql-toolbar.ql-snow .ql-active .ql-stroke,
  .ql-toolbar.ql-snow .ql-active .ql-fill {
    stroke: #579DFF;
    fill: #579DFF;
  }

  .ql-tooltip {
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
  }

  .ql-tooltip a.ql-action:hover {
    color: #579DFF;
  }

  .ql-tooltip.ql-editing a.ql-action {
    color: #579DFF;
  }

  .ql-tooltip input[type=text] {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
  }
`;

export const CollapseButton = styled.button`
    background: transparent;
    border: none;
    color: #fff;
    cursor: pointer;
    padding: 8px;
    border-radius: 3px;
    display: flex;
    align-items: center;

    &:hover {
        background: rgba(9, 30, 66, 0.08);
    }
`;

export const CollapsedInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #bbb;
  font-size: 14px;

  /* For line clamping */
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Limit to 2 lines */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis; /* Fallback, may not show with -webkit-box */
  line-height: 1.4em; /* Consistent line height */
  word-break: break-word; /* Ensure words break to wrap */
`;

export const BoardSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: ${props => props.limitColumnHeight ? 'hidden' : 'visible'};
    min-height: ${props => props.limitColumnHeight ? '0' : 'auto'};
`;

export const BoardContainer = styled.div`
    display: flex;
    gap: 10px;
    padding: 0 24px 16px;
    align-items: flex-start;
    height: ${props => props.limitColumnHeight ? '100%' : 'auto'};
    overflow-x: auto;
    overflow-y: ${props => props.limitColumnHeight ? 'hidden' : 'visible'};
    position: relative;
    margin-top: 24px;
`;

export const AddColumnButton = styled.button`
  min-width: 272px;
  height: fit-content;
  padding: 10px;
  background: rgba(255, 255, 255, 0.24);
  border: none;
  border-radius: 3px;
  color: #172b4d;
  cursor: pointer;
  font-weight: bold;
  margin-left: 8px;

  &:hover {
    background: rgba(255, 255, 255, 0.32);
  }
`;

export const SettingsButton = styled.button`
  background: transparent;
  border: none;
  color: #fff;
  padding: 8px;
  border-radius: 3px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;

  &:hover {
    background: rgba(255, 255, 255, 0.5);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const SettingsMenu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  background: white;
  border-radius: 3px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  z-index: 1000;
`;

export const MenuItem = styled.button`
  width: 100%;
  padding: 8px 16px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #172b4d;
  font-size: 14px;

  &:hover {
    background: #f4f5f7;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const HeaderControls = styled.div`
    display: flex;
    gap: 4px;
    align-items: center;
    flex-shrink: 0; /* Prevent HeaderControls from shrinking */
`;

export const SearchInputWrapper = styled.div`
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    padding: 4px 8px; /* Ensure this padding accommodates the icon area correctly */
    margin-right: 8px;
    position: relative; /* Added for precise icon positioning if needed, though flexbox handles it well here */

    svg { /* This targets the SearchIcon primarily */
        color: #fff;
        margin-right: 8px;
    }

    button > svg { /* This targets the CloseIcon inside the button */
        margin-right: 0; /* Override general svg margin if needed */
    }
    
    &.desktop-only {
        @media (max-width: 768px) {
            display: none;
        }
    }
`;

export const SearchInput = styled.input`
    background: transparent;
    border: none;
    color: #fff;
    font-size: 14px;
    padding: 4px 0; /* Adjusted padding-right for clear icon */
    outline: none;
    flex-grow: 1;

    &::placeholder {
        color: #bbb;
    }
`;

export const UrlWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
    color: #8C9BAB;
`;

export const ProjectUrl = styled.input`
    background: transparent;
    border: none;
    font-size: 14px;
    color: #bbb;
    width: 100%;
    padding: 4px;
    border-radius: 3px;

    &:hover {
        background: ${props => props.readOnly ? 'transparent' : 'rgba(255, 255, 255, 0.1)'};
    }

    &:focus {
        outline: none;
    }

    &::placeholder {
        color: #666;
    }
`;

export const ProjectUrlDisplay = styled.a`
    font-size: 14px;
    color: #579DFF; /* Link color */
    text-decoration: underline;
    cursor: pointer;
    padding: 4px;
    border-radius: 3px;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    word-break: break-all; /* Ensures long URLs wrap */

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;
